/**
 * INSTALLED-APP RETURN — the installed app opening signed out (job #129).
 *
 * iOS gives a Home Screen app its own storage, apart from Safari's. A teacher
 * who signs up in Safari, sets a password and taps Add to Home Screen opens
 * the new icon with no session and no cached role, so the app took her for a
 * stranger and pickerFirst (job #784) sent her to "Choose your course".
 * Watson, under Tom's ruling r-2026-10-07-teacher-sign-up-sign-in-must: she
 * must be able to sign in by password straight from the installed app and land
 * back in her school, not on the course picker.
 *
 * So on the app's first navigation to the bare player, an installed app with
 * no session skips the picker, opens the sign-in (password first), and once
 * the role is known a school role goes on to /schools. An installed app is
 * never a stranger: somebody used the site to install it.
 */
import { ref, watch, type Ref } from 'vue'
import type { StorageLike } from './pickerFirst'

export interface InstalledReturnInput {
  isFirstNavigation: boolean
  path: string
  queryKeys: number
  /** Running as an installed app (display-mode standalone, or iOS navigator.standalone). */
  standalone: boolean
  local: StorageLike | null
}

function hasSession(local: StorageLike): boolean {
  for (let i = 0; i < local.length; i++) {
    const k = local.key(i)
    if (k && /^sb-.+-auth-token$/.test(k) && local.getItem(k)) return true
  }
  return false
}

/** True when the installed app has opened signed out on the bare player. */
export function installedSignedOutFirstLoad(input: InstalledReturnInput): boolean {
  if (!input.standalone || !input.isFirstNavigation) return false
  if (input.path !== '/' || input.queryKeys > 0) return false
  // No storage at all: we cannot tell, so leave the ordinary landing.
  if (!input.local) return false
  try {
    return !hasSession(input.local)
  } catch {
    return false
  }
}

/** Where a just-signed-in installed app goes: a school role to /schools, anyone else stays. */
export function installedReturnTarget(hasSchoolRole: boolean): string | null {
  return hasSchoolRole ? '/schools' : null
}

export function isStandaloneWindow(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if ((window.navigator as any).standalone === true) return true
    return !!window.matchMedia?.('(display-mode: standalone)').matches
  } catch {
    return false
  }
}

/** Set by the router guard, read once by PlayerContainer to open the sign-in. */
export const installedSignInPending = ref(false)

/**
 * After the installed app's sign-in, wait for the learner row to say who this
 * is, however long that takes, then send a school role to its school. No
 * deadline (lane review #149): a fixed wait gave up on slow school wifi and
 * left the teacher in the player for good. Fires once, clears the pending
 * flag, stops watching, and calls `onDone` so the caller can drop its handle
 * — otherwise a second sign-in in the same app lifetime found a stale handle
 * and never watched again (lane review #155). Returns the stop handle for an
 * early unmount, or null when the role was already known and it finished at
 * once: onDone has then run before the caller's assignment, and handing back a
 * handle would put the stale one straight back (lane review #164).
 */
export function returnWhenRoleKnown(
  role: { isRoleAuthoritative: Readonly<Ref<boolean>>; hasSchoolRole: Readonly<Ref<boolean>> },
  push: (path: string) => unknown,
  onDone?: () => void,
): (() => void) | null {
  let stop: (() => void) | null = null
  let done = false
  stop = watch(
    () => role.isRoleAuthoritative.value,
    (known) => {
      if (!known || done) return
      done = true
      installedSignInPending.value = false
      const target = installedReturnTarget(role.hasSchoolRole.value)
      if (target) void push(target)
      stop?.()
      onDone?.()
    },
    { immediate: true },
  )
  if (done) { stop(); return null }
  return () => stop?.()
}
